import { useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CollectionsIcon from '@mui/icons-material/Collections';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import type { AnnouncementDto } from '@taqon/contracts';
import { BRAND } from '../../shared/ui/tokens';
import { dateSpan } from '../hr/AnnouncementsPage';

/**
 * An announcement opened from the dashboard (CLR-63 follow-up, 22 October 2026): its pictures as a
 * banner across the top — a carousel when there are several — and everything it says below. The
 * small pictures on the dashboard only invite the click; this is where they can be seen properly.
 */

/** What the popup shows of an event or a news item. */
export interface AnnouncementView {
  readonly title: string;
  readonly kindLabel: string;
  readonly startDate: string;
  readonly endDate: string | null;
  /** "Tomorrow", "In 3 days"…, for an event. */
  readonly when?: string | null;
  readonly location?: string | null;
  readonly taqonAttending?: boolean;
  readonly details: string | null;
  readonly linkUrl?: string | null;
  readonly document?: string | null;
  /** Picture URLs, in the order they were uploaded. */
  readonly pictures: readonly string[];
}

/** An announcement's pictures, readable by everyone who may see the announcement (CLR-63). */
export function picturesOf(item: Pick<AnnouncementDto, 'id' | 'hasPicture' | 'pictureIds'>) {
  const base = `/api/v1/hr/announcements/${encodeURIComponent(item.id)}`;
  if (item.pictureIds !== undefined) {
    return item.pictureIds.map((id) => `${base}/pictures/${encodeURIComponent(id)}`);
  }
  return item.hasPicture === true ? [`${base}/picture`] : [];
}

/**
 * Pictures one at a time, with previous/next, dots and swiping; the arrow keys move between them
 * when the carousel has focus. It never advances on its own, so nobody has to chase it. A picture
 * is shown whole (`contain`), never cropped, on a dark band.
 */
export function PictureCarousel({
  pictures,
  title,
  height,
}: {
  pictures: readonly string[];
  title: string;
  height: { xs: number; sm: number };
}) {
  const [index, setIndex] = useState(0);
  const [failed, setFailed] = useState<ReadonlySet<string>>(new Set());
  const shown = pictures.filter((p) => !failed.has(p));
  const swipeFrom = useRef<number | null>(null);
  if (shown.length === 0) return null;
  const current = Math.min(index, shown.length - 1);
  const many = shown.length > 1;
  const go = (step: number) => {
    setIndex((current + step + shown.length) % shown.length);
  };
  const onKeyDown = (event: KeyboardEvent) => {
    if (!many) return;
    if (event.key === 'ArrowLeft') go(-1);
    else if (event.key === 'ArrowRight') go(1);
    else return;
    event.preventDefault();
  };
  const onPointerDown = (event: PointerEvent) => {
    swipeFrom.current = event.clientX;
  };
  const onPointerUp = (event: PointerEvent) => {
    const from = swipeFrom.current;
    swipeFrom.current = null;
    if (!many || from === null) return;
    const moved = event.clientX - from;
    if (Math.abs(moved) > 40) go(moved < 0 ? 1 : -1);
  };
  const arrow = { position: 'absolute', top: '50%', transform: 'translateY(-50%)' } as const;
  const arrowLook = {
    color: '#fff',
    bgcolor: alpha(BRAND.charcoal, 0.55),
    '&:hover': { bgcolor: alpha(BRAND.charcoal, 0.8) },
  } as const;
  return (
    <Box
      role="region"
      aria-roledescription="carousel"
      aria-label={`Pictures of ${title}`}
      tabIndex={many ? 0 : undefined}
      onKeyDown={onKeyDown}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      sx={{
        position: 'relative',
        bgcolor: BRAND.charcoal,
        touchAction: 'pan-y',
        '&:focus-visible': { outline: `3px solid ${BRAND.mint}`, outlineOffset: -3 },
      }}
    >
      <Box
        component="img"
        key={shown[current]}
        src={shown[current]}
        alt={many ? `${title}, picture ${String(current + 1)} of ${String(shown.length)}` : title}
        draggable={false}
        onError={() => {
          const src = shown[current];
          if (src !== undefined) setFailed((before) => new Set(before).add(src));
        }}
        sx={{ display: 'block', width: '100%', height, objectFit: 'contain', userSelect: 'none' }}
      />
      {many && (
        <>
          {/* The wrapper places each arrow: the theme scales a pressed button, which would undo
              a transform on the button itself. */}
          <Box sx={{ ...arrow, left: 8 }}>
            <IconButton
              aria-label="Previous picture"
              onClick={() => {
                go(-1);
              }}
              sx={arrowLook}
            >
              <ChevronLeftIcon />
            </IconButton>
          </Box>
          <Box sx={{ ...arrow, right: 8 }}>
            <IconButton
              aria-label="Next picture"
              onClick={() => {
                go(1);
              }}
              sx={arrowLook}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>
          <Stack
            direction="row"
            spacing={0.75}
            sx={{ position: 'absolute', bottom: 10, left: 0, right: 0, justifyContent: 'center' }}
          >
            {shown.map((src, i) => (
              <ButtonBase
                key={src}
                aria-label={`Show picture ${String(i + 1)} of ${String(shown.length)}`}
                aria-current={i === current ? 'true' : undefined}
                onClick={() => {
                  setIndex(i);
                }}
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: i === current ? '#fff' : alpha('#fff', 0.45),
                  boxShadow: `0 0 0 1px ${alpha(BRAND.charcoal, 0.4)}`,
                }}
              />
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
}

/**
 * A small picture that opens the announcement: the first of its pictures, with how many there are
 * when there are several. A real button, named after the announcement, so it is reachable by
 * keyboard and announced by screen readers.
 */
export function PictureButton({
  pictures,
  title,
  onOpen,
  sx,
}: {
  pictures: readonly string[];
  title: string;
  onOpen: () => void;
  sx: object;
}) {
  const [failed, setFailed] = useState(false);
  const first = pictures[0];
  if (first === undefined || failed) return null;
  return (
    <ButtonBase
      onClick={onOpen}
      aria-label={`Open ${title}${pictures.length > 1 ? ` (${String(pictures.length)} pictures)` : ''}`}
      sx={{
        position: 'relative',
        overflow: 'hidden',
        flexShrink: 0,
        '&:hover img, &:focus-visible img': { transform: 'scale(1.04)' },
        '&:focus-visible': { outline: `3px solid ${BRAND.mint}`, outlineOffset: 2 },
        ...sx,
      }}
    >
      <Box
        component="img"
        src={first}
        alt=""
        loading="lazy"
        onError={() => {
          setFailed(true);
        }}
        sx={{
          display: 'block',
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          transition: 'transform 200ms ease',
        }}
      />
      {pictures.length > 1 && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            right: 4,
            bottom: 4,
            display: 'flex',
            alignItems: 'center',
            gap: 0.25,
            px: 0.5,
            borderRadius: 1,
            fontSize: 11,
            fontWeight: 700,
            lineHeight: 1.6,
            color: '#fff',
            bgcolor: alpha(BRAND.charcoal, 0.75),
            '& svg': { fontSize: 12 },
          }}
        >
          <CollectionsIcon />
          {pictures.length}
        </Box>
      )}
    </ButtonBase>
  );
}

function ExternalLink({ href, label, hint }: { href: string; label: string; hint: string }) {
  return (
    <Link href={href} target="_blank" rel="noopener noreferrer" sx={{ fontWeight: 600 }}>
      {label}
      <OpenInNewIcon
        fontSize="inherit"
        aria-label={hint}
        sx={{ ml: 0.25, verticalAlign: 'middle' }}
      />
    </Link>
  );
}

/** The opened announcement: the picture banner, then its details and links. */
export function AnnouncementDialog({
  view,
  onClose,
}: {
  view: AnnouncementView | null;
  onClose: () => void;
}) {
  return (
    <Dialog
      open={view !== null}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="announcement-popup-title"
      // On a phone the popup takes nearly the whole width, so the banner is as large as it can be.
      slotProps={{
        paper: {
          sx: {
            overflow: 'hidden',
            m: { xs: 1, sm: 4 },
            width: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 64px)' },
            maxHeight: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 64px)' },
          },
        },
      }}
    >
      {view !== null && (
        <>
          <PictureCarousel
            pictures={view.pictures}
            title={view.title}
            height={{ xs: 300, sm: 420 }}
          />
          <DialogContent sx={{ pt: 2.5 }}>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                <Chip size="small" label={view.kindLabel} />
                {view.taqonAttending === true && (
                  <Chip size="small" color="success" variant="outlined" label="TAQON attending" />
                )}
              </Stack>
              <Typography
                id="announcement-popup-title"
                variant="h5"
                component="h2"
                sx={{ fontWeight: 700, overflowWrap: 'anywhere' }}
              >
                {view.title}
              </Typography>
              <Typography color="text.secondary">
                {[dateSpan(view.startDate, view.endDate), view.when ?? null]
                  .filter((part) => part !== null && part !== '')
                  .join(' · ')}
              </Typography>
              {view.location != null && (
                <Typography>
                  <Box component="span" sx={{ fontWeight: 600 }}>
                    Location:
                  </Box>{' '}
                  {view.location}
                </Typography>
              )}
              {view.details !== null && (
                <Typography sx={{ whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>
                  {view.details}
                </Typography>
              )}
              {(view.linkUrl != null || view.document != null) && (
                <Stack direction="row" spacing={2.5} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                  {view.linkUrl != null && (
                    <ExternalLink
                      href={view.linkUrl}
                      label="More details"
                      hint="(opens another website)"
                    />
                  )}
                  {view.document != null && (
                    <ExternalLink
                      href={view.document}
                      label="Open PDF"
                      hint="(opens in a new tab)"
                    />
                  )}
                </Stack>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={onClose}>
              Close
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}
